// src/components/shared/auth/ResetPasswordView.jsx
import React, { useState, useEffect } from "react";
import { Shield, CheckCircle, XCircle, Lock, Eye, EyeOff } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../ui/LanguageSwitcher";
import { authService } from "../../../services/auth.service";

const ResetPasswordView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({ newPassword: "", confirmPassword: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // OTP credentials — prefer navigation state (set by ForgotPasswordView step 2)
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [credentialsReady, setCredentialsReady] = useState(false);

  useEffect(() => {
    // Primary source: React Router state (set by ForgotPasswordView after verify-otp)
    const stateEmail = location.state?.email;
    const stateOtp = location.state?.otp;

    if (stateEmail && stateOtp) {
      setEmail(stateEmail);
      setOtp(stateOtp);
      setCredentialsReady(true);
      return;
    }

    // No valid credentials — the user landed here directly without going through OTP flow
    setCredentialsReady(false);
    showMessage("error", t("validation.invalidResetLink"));
  }, [location.state, t]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 6000);
  };

  const getPasswordStrength = () => {
    const p = formData.newPassword;
    if (!p) return { strength: 0, text: "", color: "" };
    let s = 0;
    if (p.length >= 6) s++;
    if (p.length >= 8) s++;
    if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s++;
    if (/\d/.test(p)) s++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(p)) s++;
    if (s <= 2) return { strength: 33, text: t("validation.passwordStrengthWeak"), color: "bg-red-500" };
    if (s <= 3) return { strength: 66, text: t("validation.passwordStrengthMedium"), color: "bg-yellow-500" };
    return { strength: 100, text: t("validation.passwordStrengthStrong"), color: "bg-green-500" };
  };

  const validate = () => {
    if (!formData.newPassword) return t("validation.enterNewPassword");
    if (formData.newPassword.length < 6) return t("validation.passwordMinLength", { count: 6 });
    if (formData.newPassword !== formData.confirmPassword) return t("validation.passwordMismatch");
    if (!credentialsReady) return t("validation.invalidResetLink");
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { showMessage("error", err); return; }

    setLoading(true);
    try {
      // Backend: POST /Auth/reset-password { email, otp, newPassword }
      await authService.resetPassword({
        email,
        otp,
        newPassword: formData.newPassword,
      });
      showMessage("success", t("messages.passwordResetSuccess"));
      setTimeout(() => navigate("/login"), 2000);
    } catch (e) {
      showMessage("error", e?.message || t("messages.passwordResetError"));
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="relative min-h-screen text-white overflow-hidden">
      <video className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline>
        <source src="/login-bg.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-purple-900/40 to-black/80" />

      <div className="relative z-10 min-h-screen flex items-center my-6 justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-[0_0_60px_rgba(255,0,255,0.25)] rounded-2xl p-8">
            <div className="flex justify-end mb-4">
              <LanguageSwitcher />
            </div>

            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-white/20 rounded-full border border-white/30 backdrop-blur-md flex items-center justify-center mx-auto mb-4">
                <Shield className="w-10 h-10 text-pink-300" />
              </div>
              <h2 className="text-3xl font-bold mb-2">{t("auth.reset.title")}</h2>
              <p className="text-gray-200">{t("auth.reset.subtitle")}</p>
            </div>

            {message.text && (
              <div className={`mb-6 p-4 rounded-xl border text-sm flex items-start gap-3 ${
                message.type === "success"
                  ? "bg-green-500/10 border-green-400 text-green-200"
                  : "bg-red-500/10 border-red-400 text-red-200"
              }`}>
                {message.type === "success"
                  ? <CheckCircle className="w-5 h-5 shrink-0" />
                  : <XCircle className="w-5 h-5 shrink-0" />
                }
                <span>{message.text}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* New password */}
              <div>
                <label className="block text-sm mb-2">{t("auth.reset.newPasswordLabel")}</label>
                <div className="relative">
                  <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 w-5 h-5" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.newPassword}
                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                    placeholder={t("auth.reset.newPasswordPlaceholder")}
                    disabled={loading || !credentialsReady}
                    className="w-full pr-10 pl-12 py-3 bg-white/5 border border-white/20 text-white rounded-xl placeholder-gray-300 focus:ring-2 focus:ring-pink-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-white"
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>

                {formData.newPassword && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-300">{t("auth.reset.strengthLabel")}</span>
                      <span className={`text-xs font-medium ${
                        passwordStrength.strength === 100 ? "text-green-300"
                          : passwordStrength.strength === 66 ? "text-yellow-300"
                          : "text-red-300"
                      }`}>{passwordStrength.text}</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${passwordStrength.strength}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm password */}
              <div>
                <label className="block text-sm mb-2">{t("auth.reset.confirmPasswordLabel")}</label>
                <div className="relative">
                  <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 w-5 h-5" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder={t("auth.reset.confirmPasswordPlaceholder")}
                    disabled={loading || !credentialsReady}
                    className="w-full pr-10 pl-12 py-3 bg-white/5 border border-white/20 text-white rounded-xl placeholder-gray-300 focus:ring-2 focus:ring-pink-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-white"
                  >
                    {showConfirmPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>

                {formData.confirmPassword && (
                  <p className={`text-xs mt-2 flex items-center gap-1 ${
                    formData.newPassword === formData.confirmPassword ? "text-green-300" : "text-red-300"
                  }`}>
                    {formData.newPassword === formData.confirmPassword
                      ? <CheckCircle className="w-3 h-3" />
                      : <XCircle className="w-3 h-3" />
                    }
                    {formData.newPassword === formData.confirmPassword
                      ? t("validation.passwordsMatch")
                      : t("validation.passwordsMismatch")
                    }
                  </p>
                )}
              </div>

              {/* Requirements */}
              <div className="bg-white/5 border border-white/10 backdrop-blur-lg rounded-xl p-4">
                <h4 className="text-sm font-medium text-pink-200 mb-2">{t("auth.reset.requirementsTitle")}</h4>
                <ul className="text-xs text-gray-200 space-y-1">
                  <li className={formData.newPassword.length >= 6 ? "text-green-300" : ""}>
                    - {t("auth.reset.requirementMinLength", { count: 6 })}
                  </li>
                  <li className={/[A-Z]/.test(formData.newPassword) ? "text-green-300" : ""}>
                    - {t("auth.reset.requirementUppercase")}
                  </li>
                  <li className={/\d/.test(formData.newPassword) ? "text-green-300" : ""}>
                    - {t("auth.reset.requirementNumber")}
                  </li>
                </ul>
              </div>

              <button
                type="submit"
                disabled={loading || !credentialsReady}
                className="w-full py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-pink-500 to-red-500 hover:opacity-90 disabled:opacity-50 transition"
              >
                {loading ? t("auth.reset.loading") : t("auth.reset.submit")}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate("/login")}
                className="text-pink-300 hover:text-pink-100 font-medium transition"
              >
                {t("auth.backToLogin")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordView;
